import { Box } from '@mui/material';
import React from 'react';

import routes from '@/assets/constants/routes-no.ts';
import { ArrowLink } from '@/competitions/_common/components/ArrowLink.tsx';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { Timer, type TimerTheme } from '@/ui/Timer.tsx';

import { APRIL_2026_CAMPAIGN_END_DATE } from '../../../config.ts';
import { CTA } from '../_components/CTA.tsx';
import { Section } from '../_components/Section.tsx';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});
const dateStr = dateFormatter.format(APRIL_2026_CAMPAIGN_END_DATE);

const timerTheme: TimerTheme = {
  backgroundColor: '#FFFFFF',
  digitsColor: '#223C66',
  labelsColor: '#FFFFFF',
};

export type TimerSectionProps = {
  activeCompetition: CompetitionWithConfig | undefined;
  displayWarningWhenKIn: string[] | undefined;
};

export const TimerSection: React.FC<TimerSectionProps> = (props) => {
  return (
    <Section
      sx={{
        gap: '20px',
        flexDirection: { md: 'row' },
        flexWrap: { md: 'wrap', xl: 'initial' },
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          textAlign: { xs: 'center', xl: 'left' },
          flex: { xl: 1.15 },
          width: { md: '100%', xl: 'auto' },
          mb: { xs: 4, md: 2 },
          pr: { xl: 8 },
          mt: { xl: 4 },
        }}
      >
        <h2 className="mb-6 typography-v1-h1">Over halvparten får lavere rente</h2>

        <p className="mx-auto mb-8 max-w-prose typography-v1-body3">
          Over halvparten av de som sjekker med Scoopr får tilbud om lavere rente på minst ett av
          sine lån.
          <br />
          <br />
          Lavere rente gjør det enklere å bli gjeldfri. Første steg er å få oversikt over din gjeld
          og dine renter.
          <br />
          <br />
          Gjennom våre trekninger har titusenvis av nordmenn gjort nettopp dette, og flere tusen har
          fått tilbud om lavere rente.
        </p>

        <div className="flex items-end gap-8">
          <CTA
            activeCompetition={props.activeCompetition}
            displayWarningWhenKIn={props.displayWarningWhenKIn}
            onClick={() => {
              sendUserPortalEvent('apr26_competition_cta_timer_clicked');
            }}
          />
        </div>
      </Box>
      <div className="mx-auto flex max-w-sm flex-1 flex-col gap-8 rounded-lg bg-[#EFF7FF] p-6 py-8 typography-v1-body2 md:max-w-md lg:mx-0 lg:max-w-xl">
        <h2 className="-mb-2 typography-v1-h2">Slik fungerer Premiepotten</h2>
        <p>Vinneren får først slettet sin forbruks- og kredittkortgjeld.</p>
        <p>
          Har du mindre enn <b>500&nbsp;000&nbsp;kr</b> i slik gjeld? Da går resten av beløpet
          direkte til nedbetaling av boliglånet ditt.
        </p>
        <ul className="list-disc pl-6">
          <li>
            Premie: Inntil <b>500&nbsp;000&nbsp;kr</b> til gjelds-sletting.
          </li>
          <li>Inkluderer: Forbruksgjeld, kredittkort og boliglån.</li>
          <li>
            Trekning: Vinneren trekkes <b>{dateStr}</b>.
          </li>
        </ul>

        <ArrowLink to={routes.APRIL_2026_COMPETITION_TERMS} sx={{ display: 'block', mt: 'auto' }}>
          Vilkår og betingelser
        </ArrowLink>
      </div>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-[#18A0FB] p-6 py-8 typography-v1-body2 text-white md:max-w-md lg:mx-0 lg:max-w-xl">
        <h2 className="typography-v1-h2 md:mb-6">Trekningsdato</h2>
        <h2 className="mb-4 typography-v1-display md:mb-12 lg:text-6xl">{dateStr}</h2>
        <Timer date={APRIL_2026_CAMPAIGN_END_DATE} theme={timerTheme} />

        <p className="mt-4 mb-8 md:mt-16">
          Vinnerne kontaktes via e-post og mobil.
          <br />
          Vi tar hensyn til personvern og publiserer ikke vinnere uten samtykke.
        </p>

        <ArrowLink
          to={routes.APRIL_2026_COMPETITION_TERMS}
          sx={{ display: 'block', color: 'white', mt: 'auto' }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
    </Section>
  );
};
